
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NGXLogger as LoggerService } from "ngx-logger";
import { VotesService } from '../features/vote/votes.service';
import { OptionsService } from '../shared/options/options.service';
import { PollResultsService } from './poll-results.service';
import { HeaderService } from '../shared/poll-header/header.service';
import { CurrentPollService } from './current-poll.service';
import { OptionList, PollKeysClass, isResolvableVoteSlug, isSafeRtdbKey } from 'whocan-lib';
import { AuthService } from '../../user/auth.service';
import { TranslationGeneralService } from '../../shared/translation/translation.general.service';
import { TemplateService } from './poll-templates/template.service';
import { GoogleAnalyticsService } from '../../shared/services/google-analytics.service';
import { VoteData } from 'whocan-lib';
import { EssErrorService, EssTranslationService } from 'ngx-essentia';
import { UserVoteService } from '../features/vote/user-vote.service';
import { PollParticipantsService } from '../shared/poll-header/poll-participants.service';
import { VoteCountsService } from './vote-counts.service';
import { VoteSlugService } from './vote-slug.service';

import { PollConnectionError, PollHeaderWriteError, PollNotFoundError, isNetworkRequestFailed } from './poll-errors';
import { isPermissionDenied } from '../../shared/services/db-errors';
// re-exported so existing call sites (enter-poll.component) keep importing them from here
export { PollConnectionError, PollHeaderWriteError, PollNotFoundError, isNetworkRequestFailed } from './poll-errors';

/** read-sequence retries of the poll init after a permission_denied (workorder-poll-init-auth-drop-hotfix-2026-10-29) */
const MAX_AUTH_DROP_RETRIES = 2;
/** how long each retry waits for the signed-in user to come back */
const AUTH_DROP_WAIT_MS = 5000;

/**
 * retries of one read stage after an auth drop (0-2) and the time spent waiting for the user - per stage, never added up.
 * tokenRefreshed: a forced token refresh ran before a retry (a denial with a signed-in user, workorder 2026-11-03 task 3b);
 * tokenExpiresInMs: the token's remaining validity at the first denial (undefined = no denial yet)
 */
interface AuthDropRetryState { retries: number; waitedForUserMs: number; tokenRefreshed?: boolean; tokenExpiresInMs?: number | null }

/** the console lines of one caller of readWithAuthDropRetry; context = what may go into the line (ids, never the slug) */
interface AuthDropRetryLog {
    denied: string;
    gaveUp: string;
    succeeded: string;
    context: unknown[];
    diagnostics: () => string;
}

/**
 * An error as one line for /uilogging, in the form of the first line of a V8 stack (`Error: permission_denied at …`).
 * The logger sends an Error as its `stack`, and WebKit's stack has no "Name: message" line - 18 of 20 iOS reports
 * arrived without any error text (auth-drop hotfix, task 3). A plain object would land on a line of its own on the
 * server and take the text out of the first line the triage signatures match on. Paths stay unmasked - the triage
 * masks them itself. The code is appended only when the message does not carry it already.
 */
function errorText(error: unknown): string {
    if (error === null || error === undefined) return String(error);
    if (typeof error !== 'object') return String(error);
    const { name, code, message } = error as { name?: unknown; code?: unknown; message?: unknown };
    const text = (typeof name === 'string' && name ? name : 'Error') + ': ' + (typeof message === 'string' ? message : '');
    const codeText = typeof code === 'string' || typeof code === 'number' ? String(code) : '';
    return codeText && !text.toLowerCase().includes(codeText.toLowerCase()) ? text + ' (' + codeText + ')' : text;
}

@Injectable({
    providedIn: 'root'
})
/**
 * Service to create, inititiallize, copy etc  polls.
 * By initiializing the poll we load the data
 * The data is not hold directly here but in the unlerlying OptionsService, VotesService etc
 *
 */
export class PollService {
    private _isInitializing: boolean = false
    public pollExist = false;
    constructor(
        public authService: AuthService,
        private logger: LoggerService,
        private currentPollService: CurrentPollService,
        public votesService: VotesService,
        private optionsService: OptionsService,
        public pollResultsService: PollResultsService,
        private voteService: VotesService,
        private userVoteService: UserVoteService,
        public headerService: HeaderService,
        private transDepricated: TranslationGeneralService,
        private pollParticipantsService: PollParticipantsService,
        public templateService: TemplateService,
        private googleAnayltics: GoogleAnalyticsService,
        private errorService: EssErrorService,
        private voteCountsService: VoteCountsService,
        private voteSlugService: VoteSlugService,
    ) {
    }

    public initNewPoll(pollTemplateKey: string, pollTitle: string, themeKey?: string) {
        this.logger.log('initNewPoll', pollTemplateKey, pollTitle, themeKey);
        // this is important because we try to delte the current poll when canceling the create poll wizard
        this.unInitializePoll();
        this.templateService.initTemplate(pollTemplateKey);
        this.headerService.initNewHeader(pollTemplateKey, pollTitle, themeKey)

    }


    /** the creation that is currently running - every caller awaits the same one (see persistNewPoll) */
    private newPollInFlight: Promise<void> | null = null;

    /**
     * Creates the poll of the current header in the database: anonymous sign-up if needed, one
     * push id, header, default options, GA event.
     *
     * Single-flight: the create stepper triggers save() from two paths (selectionChange and the
     * Next/Skip button) and edit-header only flips isNewPoll after the whole run, which takes 1-2 s
     * for a first-time user. A second call while the first one runs used to draw a second push id
     * and write a second header (workorder-persist-new-poll-single-flight-2026-09-22, task 1) - now
     * it awaits the running creation. Pattern as UserService.ensureDbUser.
     *
     * Rejects when the header could not be written (after one retry, task 4): the caller keeps
     * isNewPoll, shows the error and does not navigate to a poll without a header.
     */
    public persistNewPoll(): Promise<void> {
        if (this.newPollInFlight) {
            this.logger.log('persistNewPoll: already running, awaiting it');
            return this.newPollInFlight;
        }
        this.newPollInFlight = this.persistNewPollOnce().finally(() => { this.newPollInFlight = null; });
        return this.newPollInFlight;
    }

    private async persistNewPollOnce(): Promise<void> {
        this.logger.log('persistNewPoll');
        if (!this.authService.isLoggedIn) {
            await this.authService.signUpAnonymous('');
        }
        const ownerId = await this.authService.getUid();
        const pollId = this.headerService.getNewHeaderKey(ownerId);
        this.currentPollService.setCurrentPoll(ownerId, pollId);
        this.headerService.headerClass.ownerId = ownerId
        this.headerService.headerClass.pollId = pollId
        let saved = await this.headerService.persist();
        if (!saved) {
            // the first auth-bound write of a fresh session was rejected: wait until the SDK reports
            // the connection as established, then exactly one retry with the SAME push id. No forced
            // token refresh here - that round trip is rate limited (auth/quota-exceeded). A second
            // rejection is diagnosed via the fields in the HeaderService.persist error log.
            this.logger.warn('persistNewPoll: header write denied once, retrying', ownerId, pollId);
            await this.headerService.waitForConnected(3000);
            await new Promise(resolve => setTimeout(resolve, 500));
            saved = await this.headerService.persist();
        }
        if (!saved) {
            // no options, no GA event, no navigation - the header does not exist. Not reported again:
            // HeaderService.persist logged both denials with the diagnostic fields
            throw new PollHeaderWriteError(ownerId, pollId, this.transDepricated.translate('internalError'));
        }
        this.optionsService.optionList = this.templateService.getDefaultOptionList(this.headerService.headerClass.timeZone)
        // The option list was REPLACED (potluck, order and lunchGroup bring 2 / 2 / 6 default options), and
        // the vote form only follows optionsUpdated$, which nothing emits here - initNewPoll merely
        // unsubscribed it. Worse than the length: the form is still the object of whatever the session
        // did before (answers, name, contact fields and private comment of a vote on another poll come
        // along, isNewVote stays false). So the form is rebuilt for the new poll - the same call the
        // regular path makes in loadPollData. No database read: the voter has no vote here, so
        // loadUserVotePrivateData returns at once (workorder-create-stepper-vote-form-choicelist-2026-10-06).
        await this.userVoteService.initUserVote();
        await this.optionsService.persist(true);
        this.googleAnayltics.emitPollCreated(this.headerService.headerClass.pollTemplate, this.headerService.headerClass.theme)
        // participant link slug (concept-custom-participant-links-2026-10-28 E7): its own write AFTER the header
        // exists - never part of the first header write (index first, header second; persistNewPoll double run
        // analysis 2026-09-22). ensureForCurrentPoll never throws; the catch only guards the creation against a bug
        try {
            await this.voteSlugService.ensureForCurrentPoll();
        } catch (error) {
            this.logger.warn('persistNewPoll: participant link slug not created', ownerId, pollId, error);
        }
    }

    /**
     * Makes sure the user is loigged in , checks if the poll exits and inits the PathService
     * Throws anexception if the poll does not exist
     * @param routeParam
     */
    public async enterPollByRoute(activatedRoute: ActivatedRoute) {
        const voteSlug = activatedRoute.snapshot.params['voteSlug'];
        if (voteSlug !== undefined) {
            await this.enterPollByVoteSlug(voteSlug);
            return;
        }
        const ownerId = activatedRoute.snapshot.params['ownerId'];
        const pollId = activatedRoute.snapshot.params['pollId'];
        let voteUserId = activatedRoute.snapshot.params['voteUserId'];
        if (!isSafeRtdbKey(ownerId ?? '') || !isSafeRtdbKey(pollId ?? '') || (voteUserId && !isSafeRtdbKey(voteUserId))) {
            // malformed link (relative manifest.webmanifest resolved against the vote url, a copied
            // line break, ...) - a user state like a deleted poll, never a reported error. Caught
            // here before the ids reach afd.database.ref(), which throws synchronously on . # $ [ ]
            this.logger.warn('enterPollByRoute: invalid route params', ownerId, pollId, voteUserId);
            throw new PollNotFoundError(ownerId, pollId, this.transDepricated.translate('poll.notExist') + ": " + ownerId + "/" + pollId);
        }
        if (!voteUserId) { voteUserId = this.authService.uid }
        this.logger.log('enterPollByRoute onwerId:', ownerId, 'pollId: ', pollId, 'voteUserId', voteUserId);
        await this.initPollAndSubscribeData(ownerId, pollId, voteUserId);
    }

    /** the slug resolution that is currently running - a second init of the same route awaits it */
    private voteSlugEntryInFlight: { voteSlug: string; promise: Promise<void> } | null = null;

    /**
     * `/vote/{voteSlug}` (concept-custom-participant-links-2026-10-28 §5.1): sign-in, `pollSlugs/{slug}` → ids →
     * the regular init with the own uid as vote user. The url stays the short one (no replaceUrl) - participants
     * copy it from the address bar; every navigation from here builds id paths as before.
     *
     * Single-flight per slug: the vote page may init twice (pre-existing NG0100 double init) and the second
     * call must not return before the first one has resolved and loaded the poll.
     * The slug itself never goes into a log line - only its length.
     */
    private enterPollByVoteSlug(voteSlug: string): Promise<void> {
        if (this.voteSlugEntryInFlight?.voteSlug === voteSlug) {
            this.logger.log('enterPollByVoteSlug: already resolving, awaiting it');
            return this.voteSlugEntryInFlight.promise;
        }
        const promise = this.enterPollByVoteSlugOnce(voteSlug).finally(() => {
            if (this.voteSlugEntryInFlight?.promise === promise) this.voteSlugEntryInFlight = null;
        });
        this.voteSlugEntryInFlight = { voteSlug, promise };
        return promise;
    }

    private async enterPollByVoteSlugOnce(voteSlug: string): Promise<void> {
        const notFound = () => new PollNotFoundError(undefined, undefined, this.transDepricated.translate('poll.notExist'));
        // resolving, not creating: format + hard list, the soft list must not lock out an existing poll (report S2 §6.1).
        // Checked before the slug reaches afd.database.ref(), which throws synchronously on . # $ [ ]
        if (!isResolvableVoteSlug(voteSlug ?? '')) {
            this.logger.warn('enterPollByRoute: invalid vote slug, length', (voteSlug ?? '').length);
            throw notFound();
        }
        let target: { ownerId: string; pollId: string } | null | undefined;
        let signedUpAt: number | null = null;
        /** the slug read's own retry counters - initPollAndSubscribeData counts its reads separately (hotfix 2, task 1) */
        const retryState: AuthDropRetryState = { retries: 0, waitedForUserMs: 0 };
        try {
            // the index read needs auth != null - sign in first, as initPollAndSubscribeData does for the header
            if (!(await this.authService.getIsLoggedIn())) {
                await this.authService.signUpAnonymous('');
                signedUpAt = Date.now();
            }
            // the same auth drop as on the header read hits the index read first (hotfix 2, task 1)
            target = await this.readWithAuthDropRetry(() => this.voteSlugService.resolve(voteSlug), retryState, {
                denied: 'enterPollByRoute: vote slug read denied, waiting for the signed-in user and retrying',
                gaveUp: 'enterPollByRoute: no signed-in user after the auth drop, giving up',
                succeeded: 'enterPollByRoute: vote slug resolved after the auth drop',
                context: [],
                diagnostics: () => this.initDiagnostics(signedUpAt, retryState),
            });
        } catch (error) {
            if (isNetworkRequestFailed(error)) {
                this.logger.warn('enterPollByRoute: vote slug not resolved, no connection', globalThis.navigator?.onLine);
                throw new PollConnectionError(undefined, undefined, error);
            }
            // the firebase message carries the path - the slug is masked like in the ogProxy log (report S2 §5.2)
            const message = String(error?.message ?? error).split(voteSlug).join('{slug}');
            this.logger.error('enterPollByRoute: vote slug not resolved', error?.code ?? '', message, this.initDiagnostics(signedUpAt, retryState));
            // reported above - the user gets the retry instead of the spinner (hotfix 2, task 2)
            if (isPermissionDenied(error)) throw new PollConnectionError(undefined, undefined, error, 'auth');
            throw error;
        }
        if (!target) {
            // deleted poll, mistyped or dropped alias - the same user state as a stale id link
            this.logger.warn('enterPollByRoute: vote slug not found, length', voteSlug.length);
            throw notFound();
        }
        this.logger.log('enterPollByRoute via vote slug', target.ownerId, target.pollId);
        await this.initPollAndSubscribeData(target.ownerId, target.pollId, this.authService.uid);
    }

    public pollIsLoaded(): boolean {
        //        return (!this._isInitializing) && this.pollExist;
        return (!this._isInitializing) && this.userVoteService.userVote !== null && this.pollExist
    }

    /**
     * Checks if the poll exits,  inits the currentPollService and subscribe to Poll Data
     * @param ownerId
     * @param pollId
     */
    public async initPollAndSubscribeData(ownerId: string, pollId: string, voteUserId?: string) {
        if (!this._isInitializing) {
            if (this.currentPollService.getIsCurrentPoll(ownerId, pollId, voteUserId)) return
            this._isInitializing = true
            /** Date.now() of the anonymous sign-up of THIS init - null when the session already existed */
            let signedUpAt: number | null = null;
            /** retries of the read sequence after an auth drop (0-2) and the time spent waiting for the user */
            const retryState: AuthDropRetryState = { retries: 0, waitedForUserMs: 0 };
            // the finally makes sure _isInitializing never stays true when loading throws
            // (e.g. corrupt poll data) - otherwise pollIsLoaded() stays false forever and
            // the page hangs in the loading spinner while retries are blocked
            try {
                // awaited: setCurrentPoll sets isOwner only after its own await, and the
                // header load must not start before that
                await this.initPoll(ownerId, pollId, voteUserId);
                this.logger.log('initPollAndSubscribeData', ownerId, pollId, voteUserId);
                if (!(await this.authService.getIsLoggedIn())) {// create an anonymous user for now
                    await this.authService.signUpAnonymous('');
                    signedUpAt = Date.now();
                }
                await this.loadPollWithAuthDropRetry(ownerId, pollId, voteUserId, signedUpAt, retryState);
            } catch (error) {
                if (error instanceof PollNotFoundError) {
                    // expected state (deleted poll, stale link) - no error report to /uilogging
                    this.logger.warn('initPollAndSubscribeData: poll not found', ownerId, pollId);
                } else if (isNetworkRequestFailed(error)) {
                    // no connection (offline, timeout, a blocker on identitytoolkit) - an environment state;
                    // the call site offers a retry, nothing to report to /uilogging
                    this.logger.warn('initPollAndSubscribeData: no connection', ownerId, pollId, globalThis.navigator?.onLine);
                    throw new PollConnectionError(ownerId, pollId, error);
                } else {
                    // the diagnostics go last so the message still starts with the ids (error triage signature);
                    // the error as text, not as object: WebKit's stack carries no "Name: message" line (task 3)
                    this.logger.error('initPollAndSubscribeData failed', ownerId, pollId, errorText(error),
                        this.initDiagnostics(signedUpAt, retryState));
                    // reported above - the user gets the retry instead of the spinner (hotfix 2, task 2)
                    if (isPermissionDenied(error)) throw new PollConnectionError(ownerId, pollId, error, 'auth');
                }
                throw error;
            } finally {
                this._isInitializing = false
            }
        } else {
            this.logger.warn('initPollAndSubscribeData poll is intitialized already', ownerId, pollId, voteUserId);
        }
    }

    /**
     * checkAndLoadPoll under the auth-drop retry. The uid check runs before each retry: the drop is expected to bring the
     * same uid back; another one is loaded without an error and without a report, a poll switch during the wait ends here.
     */
    private async loadPollWithAuthDropRetry(ownerId: string, pollId: string, voteUserId: string | undefined,
        signedUpAt: number | null, retryState: AuthDropRetryState): Promise<void> {
        const uidBeforeDrop = this.authService.uid;
        await this.readWithAuthDropRetry(() => this.checkAndLoadPoll(ownerId, pollId, voteUserId), retryState, {
            denied: 'initPollAndSubscribeData: read denied, waiting for the signed-in user and retrying',
            gaveUp: 'initPollAndSubscribeData: no signed-in user after the auth drop, giving up',
            succeeded: 'initPollAndSubscribeData: loaded after the auth drop',
            context: [ownerId, pollId],
            diagnostics: () => this.initDiagnostics(signedUpAt, retryState),
        }, async uid => {
            if (!this.currentPollService.getIsCurrentPoll(ownerId, pollId, voteUserId)) {
                this.logger.warn('initPollAndSubscribeData: poll changed before the retry', ownerId, pollId);
                return false;
            }
            if (uidBeforeDrop && uid !== uidBeforeDrop) {
                this.logger.warn('initPollAndSubscribeData: uid changed during the auth drop', ownerId, pollId);
                if (voteUserId === uidBeforeDrop) {
                    voteUserId = uid;
                    await this.initPoll(ownerId, pollId, voteUserId);
                }
            }
            return true;
        });
    }

    /**
     * One read stage, repeated up to MAX_AUTH_DROP_RETRIES times on permission_denied - in a fresh AND in an existing
     * session (workorder-poll-init-auth-drop-hotfix-2026-10-29 task 1, hotfix 2 task 1: the vote-slug read too). Since
     * rules-hardening-1 header/options/votes and the pollSlugs index need auth != null, and the Auth SDK can drop the
     * signed-in user for 0.5-2.5 s right after the sign-in or the restore from persistence (iOS/Safari, IndexedDB sync) -
     * the connection is up, but unauthenticated. So each retry waits for a signed-in user, not for the connection. Every
     * read is a fresh once()/firstValueFrom listen - a listen the server cancelled with permission_denied is gone, so
     * re-running is a re-subscribe. Other errors, and a user that does not come back, throw the original error.
     * beforeRetry (optional) runs with the uid that came back; false ends the stage without a value (undefined).
     */
    private async readWithAuthDropRetry<T>(read: () => Promise<T>, retryState: AuthDropRetryState, log: AuthDropRetryLog,
        beforeRetry?: (uid: string) => Promise<boolean>): Promise<T | undefined> {
        for (let attempt = 0; ; attempt++) {
            try {
                const value = await read();
                if (attempt > 0) {
                    // console only (warn does not reach /uilogging) - the line the dev check and the iPhone test look for
                    this.logger.warn(log.succeeded, ...log.context, log.diagnostics());
                }
                return value;
            } catch (error) {
                if (!isPermissionDenied(error)) throw error;
                if (retryState.tokenExpiresInMs === undefined) retryState.tokenExpiresInMs = this.authService.tokenExpiresInMs;
                if (attempt >= MAX_AUTH_DROP_RETRIES) throw error;
                // the SDK still holds a user although the server refused the read: the connection carries no (valid) auth
                // - waiting for the user would retry with the same connection auth (pattern B, workorder 2026-11-03)
                const userAtDenial = this.authService.sdkCurrentUserUid !== null;
                this.logger.warn(log.denied, ...log.context, log.diagnostics());
                const waitStartedAt = Date.now();
                const uid = await this.authService.waitForSignedInUser(AUTH_DROP_WAIT_MS);
                retryState.waitedForUserMs += Date.now() - waitStartedAt;
                if (uid === null) {
                    this.logger.warn(log.gaveUp, ...log.context);
                    throw error;
                }
                if (userAtDenial && !retryState.tokenRefreshed) {
                    // once per stage; forceTokenRefreshOnce caps it at once a minute across the app
                    retryState.tokenRefreshed = await this.authService.forceTokenRefreshOnce();
                }
                if (beforeRetry && !(await beforeRetry(uid))) return undefined;
                retryState.retries = attempt + 1;
            }
        }
    }

    /** existence check plus the data load - the unit the auth-drop retry of initPollAndSubscribeData repeats */
    private async checkAndLoadPoll(ownerId: string, pollId: string, voteUserId?: string): Promise<void> {
        this.pollExist = await this.pollExists(ownerId, pollId);
        if (this.pollExist) {
            this.logger.log('enterPoll : initService voteUserId', voteUserId);
            await this.loadPollData(ownerId, pollId, voteUserId);
        } else {
            throw new PollNotFoundError(ownerId, pollId, this.transDepricated.translate('poll.notExist') + ": " + ownerId + "/" + pollId);
        }
    }

    /**
     * Context of a failed poll init for /uilogging (rules-hardening-1b task 0b, auth-drop hotfix task 1 + 2): no uid,
     * no address - only timings, counts and yes/no. msSinceSignUp 'none' = the session existed before this init
     * (no sign-up here); retries = read sequences repeated after a denial (0-2), waitedForUserMs = time spent in
     * waitForSignedInUser; authDrops/lastDropMs/persistence from AuthService.authDropDiagnostics.
     * The vote-slug read and the poll init each pass their own retry state.
     */
    private initDiagnostics(signedUpAt: number | null, retry: AuthDropRetryState): string {
        const msSinceAuthChange = this.authService.msSinceAuthStateChange;
        return 'msSinceSignUp: ' + (signedUpAt === null ? 'none' : Date.now() - signedUpAt)
            + '; msSinceAuthChange: ' + (msSinceAuthChange ?? 'null')
            + '; currentUser: ' + (this.authService.sdkCurrentUserUid ? 'yes' : 'no')
            + '; retries: ' + retry.retries + '; waitedForUserMs: ' + retry.waitedForUserMs
            + '; connected: ' + this.headerService.connected
            + '; ' + this.authService.authDropDiagnostics
            + '; tokenRefreshed: ' + (retry.tokenRefreshed === true)
            + '; tokenExpiresInMs: ' + (retry.tokenExpiresInMs ?? 'null')
            + '; ' + this.headerService.connectionTimingDiagnostics;
    }

    public unInitializePoll() {
        this.headerService.stopDeleteWatch();
        this.headerService.headerClass?.uninitialize()
        this.optionsService.uninitialize();
        this.voteService.uninitialize();
        this.userVoteService.uninitialize();
        this.voteCountsService.uninitialize();
        this.currentPollService.unInitializeCurrentPoll();
    }

    /**
     * Initialisierung fuer Sample-Polls.
     * Laeuft fast identisch zum normalen Vote-Page-Init (inkl. anonymem Auto-Signup
     * und UserVote-Init), damit die Sample-Page visuell exakt der normalen Vote-Page
     * entspricht. Der einzige Unterschied wird ueber das isSampleMode-Flag in
     * CurrentPollService gesteuert: VotesValidationService.saveAndNavigate erkennt
     * das Flag und unterdrueckt den RTDB-Persist, zeigt stattdessen einen
     * 'Demo — kopier den Poll'-Dialog.
     *
     * @returns true wenn der Poll existiert und geladen wurde, sonst false.
     */
    public async initSamplePoll(ownerId: string, pollId: string): Promise<boolean> {
        this.logger.log('initSamplePoll', ownerId, pollId);
        try {
            await this.initPollAndSubscribeData(ownerId, pollId);
            this.currentPollService.setSampleMode(true);
            return this.pollExist;
        } catch (e) {
            this.logger.warn('initSamplePoll: load failed', ownerId, pollId, e);
            return false;
        }
    }

    public async initPoll(ownerId: string, pollId: string, voteUserId?: string) {
        if (this.currentPollService.getIsCurrentPoll(ownerId, pollId, voteUserId)) return false
        this.logger.log('initPoll', ownerId, pollId, voteUserId);
        this.unInitializePoll();
        await this.currentPollService.setCurrentPoll(ownerId, pollId, voteUserId);
        return true
    }

    private async loadPollData(ownerId: string, pollId: string, voterUid?: string) {
        // a logout or a switch to another poll (create-poll, my-polls) during one of the
        // awaits uninitializes CurrentPollService; every step re-checks and bails out
        // silently instead of subscribing to options/null/null
        const pollChanged = (step: string): boolean => {
            if (this.currentPollService.getIsCurrentPoll(ownerId, pollId, voterUid)) return false
            this.logger.warn(`loadPollData: poll changed during ${step}`, ownerId, pollId);
            return true
        }
        if (pollChanged('poll exists check')) return;
        await this.headerService.load();
        if (pollChanged('header load')) return;
        await this.optionsService.load(ownerId, pollId);
        if (pollChanged('options load')) return;
        await this.votesService.load();
        if (pollChanged('votes load')) return;
        await this.userVoteService.initUserVote(voterUid);
        if (pollChanged('user vote init')) return;
        this.votesService.subscribeToRealtimeUpdates();
        this.optionsService.subscribeToRealtimeUpdates(ownerId, pollId);
        // participant on a poll with showParticipants=false: the vote list only holds the
        // own vote, so the limit checks are fed from the public voteCounts aggregates
        // instead (workorder-option-counts-2026-08-31). Everybody else keeps the
        // list-based path untouched.
        if (!this.votesService.canReadVotesList) {
            this.voteCountsService.subscribe();
        }
    }

    public async pollExists(ownerId: string, pollId: string): Promise<boolean> {
        this.logger.log('surveyExists? start; ownerId: ' + ownerId + ' surveId: ' + pollId);
        // initPollAndSubscribeData reports a failed read itself, with the auth-window diagnostics
        return this.headerService.headerExist(ownerId, pollId, false);
    }

    public generatePollId(ownerId?: string): string {
        this.logger.log('generatePollId');
        return this.headerService.getNewHeaderKey(ownerId);
    }

    /**
     * All writes are awaited: callers reload their poll list right afterwards
     * (my-polls, context-action-bar), and an un-awaited header delete let that
     * reload run against still-existing data.
     */
    public async deletePoll(ownerId: string, pollId: string) {
        // before the first write: from here on every save of this poll is dropped - the vote preview auto-saves from
        // ngOnDestroy right after the delete, and that full header write onto the deleted path used to bring the poll back
        // (workorder-header-persist-and-poll-init-denied-with-user-2026-11-03, task 0). Taken back if the delete fails.
        this.currentPollService.markDeleted(ownerId, pollId);
        try {
            await this.deletePollWrites(ownerId, pollId);
        } catch (error) {
            this.currentPollService.unmarkDeleted(ownerId, pollId);
            throw error;
        }
    }

    private async deletePollWrites(ownerId: string, pollId: string) {
        // participant link slugs first: for a co-organizer the index delete hangs on the grant, which goes with
        // pollAccess after the header (report S1 §7.4). Never throws - the orphan sweep is the net
        const loadedHeader = this.headerService.headerClass;
        const isLoaded = !!loadedHeader?.headerData && loadedHeader.ownerId === ownerId && loadedHeader.pollId === pollId;
        await this.voteSlugService.removeAllForPoll(ownerId, pollId, isLoaded ? loadedHeader.headerData : undefined);
        // loop all the votes to delete all the participations
        const votes = await this.voteService.getVotes(ownerId, pollId);
        // allSettled, not all: the deletability of the poll must not hang on a stale
        // participation index entry. A denied participation delete leaves an orphaned
        // entry behind — the lesser evil (status quo before 08-21), now visibly logged.
        const participationResults = await Promise.allSettled(votes.map(vote =>
            this.pollParticipantsService.deleteParticipation(ownerId, pollId, vote.userId)
        ));
        participationResults.forEach((result, index) => {
            if (result.status === 'rejected') {
                this.logger.warn('deletePoll: participation delete failed',
                    { ownerId, pollId, voteUserId: votes[index].userId, reason: result.reason });
            }
        });
        // one atomic write for header, options, votes and votePrivate: the lists read the
        // header, so it still vanishes together with everything else — and the
        // vote-confirmation trigger never sees votes disappearing under a live header
        await this.headerService.deletePollData(ownerId, pollId);
    }

    async copyPoll(ownerId: string, pollId: string): Promise<PollKeysClass> {
        this.logger.log('copyPoll: ', pollId);
        // the /poll-templates listing offers "copy and edit" before any sign-in (rules-hardening-1b task 2)
        if (!this.authService.isLoggedIn) {
            await this.authService.signUpAnonymous('');
        }
        const newOwnerId = await this.authService.getUid();
        if (!newOwnerId) {
            // never a write with undefined in the path (whoo/header/undefined/...) - same guard as isPersistableIndex
            throw this.errorService.newError('copyPoll: no signed-in uid');
        }
        const newHeader = await this.headerService.copyHeader(ownerId, pollId, newOwnerId, this.authService.displayName);
        if (newHeader) {
            const copiedOptionList = await this.optionsService.persistCopyOfOptionList(ownerId, pollId, newOwnerId, newHeader.pollId, newHeader.timeZone);
            if (copiedOptionList) {
                this.logger.log('SurveyOptions Copied');
            } else {
                this.logger.warn('copyPoll: options could not be copied for poll', pollId);
            }
            return new PollKeysClass({ ownerId: newOwnerId, pollId: newHeader.pollId });
        } else {
            throw (new Error(this.transDepricated.translate('poll.notExist')));
        }
    }

}
